import { useEffect } from "react";
import { Link } from "react-router-dom";
import "../styles/home.css";

const Home = () => {
  useEffect(() => {
    document.title = "Disciplined Me, Achieve Your Goals with AI as Your Personal Coach";
  }, []);

  return (
    <div className="site-page page-home">
      <nav>
        <div className="wrap">
          <div className="logo">disciplined<span>.me</span></div>
          <div className="navlinks">
            <a href="#books">The books</a>
            <a href="#why">Why discipline</a>
            <a href="#story">My story</a>
          </div>
          <Link to="/free-guide" className="nav-cta">Get the free guide</Link>
        </div>
      </nav>

      <section className="hero">
        <div className="wrap">
          <div className="hero-badge"><span className="dot"></span> Built from a real 30-day rebuild, not theory</div>
          <h1>You don't need more<br />motivation. You need <span className="hl">a system.</span></h1>
          <p className="hero-sub">Disciplined Me teaches students and young professionals how to build unshakeable discipline, using AI as a daily coach, not just a productivity gimmick.</p>
          <div className="hero-actions">
            <Link to="/free-guide" className="btn btn-primary">Get the free AI prompts guide →</Link>
            <Link to="/bundle" className="btn btn-secondary">See the full book</Link>
          </div>
          <div className="tracker">
            <div className="tnode"><div className="tcircle filled"></div><div className="tlabel">DAY 1</div></div>
            <div className="tline"></div>
            <div className="tnode"><div className="tcircle"></div><div className="tlabel">DAY 7</div></div>
            <div className="tline"></div>
            <div className="tnode"><div className="tcircle"></div><div className="tlabel">DAY 14</div></div>
            <div className="tline"></div>
            <div className="tnode"><div className="tcircle"></div><div className="tlabel">DAY 30</div></div>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="wrap">
          <h2>Motivation gets you started.<br />It never gets you to Day 30.</h2>
          <p>You've set the goal before. You've felt the momentum before. And you've watched it disappear the moment life got hard, a bad week, a bad night's sleep, one missed day that turned into a month.</p>
          <span className="lora">"It wasn't your fault. It was the wrong tool."</span>
        </div>
      </section>

      <section className="explain">
        <div className="wrap">
          <div className="explain-head">
            <div className="eyebrow">How it works</div>
            <h2>Three shifts. One system.</h2>
          </div>
          <div className="pillars">
            <div className="pillar">
              <div className="pillar-num" style={{ background: "var(--purple)" }}>01</div>
              <h3>Systems, not willpower</h3>
              <p>Willpower depletes by evening. A system doesn't care how you feel, it just runs. Every chapter shows you how to build one.</p>
            </div>
            <div className="pillar">
              <div className="pillar-num" style={{ background: "var(--teal)" }}>02</div>
              <h3>AI as your daily coach</h3>
              <p>Not a search engine. A configured, ongoing coaching relationship, with the exact prompts to make it real, starting today.</p>
            </div>
            <div className="pillar">
              <div className="pillar-num" style={{ background: "var(--coral)" }}>03</div>
              <h3>Never miss twice</h3>
              <p>You will fail sometimes. The system isn't about perfection, it's about the one rule that gets you back on track by tomorrow.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="books" id="books">
        <div className="wrap">
          <div className="books-head">
            <div className="eyebrow">Start here</div>
            <h2>Two ways in, start free, go deeper when ready</h2>
          </div>
          <div className="book-grid">
            <div className="book-card">
              <div className="book-cover free">
                <span className="price-tag free">Free</span>
                <div className="cover-mock">
                  <img src="/images/free-guide-cover.png" alt="7 AI Prompts That Build Discipline — cover" />
                </div>
              </div>
              <div className="book-body">
                <h3>The Free Prompts Guide</h3>
                <p>Seven ready-to-use AI prompts that turn any chat tool into a daily accountability coach. Copy, paste, start tonight.</p>
                <ul className="book-list">
                  <li>Morning + evening coaching prompts</li>
                  <li>The focus reset for any distraction spiral</li>
                  <li>Delivered instantly, free forever</li>
                </ul>
                <Link to="/free-guide" className="book-btn outline">Send me the free guide</Link>
              </div>
            </div>

            <div className="book-card featured">
              <div className="book-cover paid">
                <span className="price-tag">CHF 29</span>
                <div className="cover-mock">
                  <img src="/images/ebook-cover.png" alt="The Disciplined Mind — e-book cover" />
                </div>
              </div>
              <div className="book-body">
                <h3>The Complete Bundle</h3>
                <p>The full e-book plus the companion workbook, the entire system, and the exercises that make it stick for 30 real days.</p>
                <ul className="book-list">
                  <li>5 chapters + 12 ready-to-use AI prompts</li>
                  <li>9 guided workbook exercises</li>
                  <li>Full 30-day tracker included</li>
                </ul>
                <Link to="/bundle" className="book-btn">Get the bundle, CHF 29</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="story" id="story">
        <div className="wrap">
          <div className="story-visual">
            <span className="lora">"I lost my job while recovering from knee surgery. Lying on that couch, I realized I had never actually learned how to achieve a goal, I had built on willpower, and willpower is the most unreliable resource you own."</span>
            <span className="attrib">Stefan, founder of Disciplined Me</span>
          </div>
          <div className="story-copy">
            <div className="eyebrow">Why this exists</div>
            <h2>Built from a real rebuild, not a content calendar</h2>
            <p>Everything in this system exists because it solved a real problem. Not because it sounded good, because it worked, on the days I had every reason to quit.</p>
            <p>I rebuilt from zero using one thing: a system that didn't depend on how I felt. AI became the coach I couldn't afford to hire. This is that exact system, written down.</p>
            <Link to="/bundle" className="btn btn-primary" style={{ marginTop: "8px" }}>Read the full story in the book</Link>
          </div>
        </div>
      </section>

      <section className="why" id="why">
        <div className="wrap">
          <div className="why-head">
            <div className="eyebrow" style={{ color: "var(--gold)" }}>Why discipline</div>
            <h2>Why this matters more than any single goal</h2>
            <p>Discipline is the one skill that transfers to everything you'll ever want to build.</p>
          </div>
          <div className="why-grid">
            <div className="why-item"><h3><i>—</i>It's the only skill that transfers</h3><p>Motivation is goal-specific and runs out. A real system works whether you're getting fit, building a business, or finishing a degree.</p></div>
            <div className="why-item"><h3><i>—</i>It compounds silently</h3><p>Small consistent actions don't feel significant day to day, until the gap between 80% and 20% consistency becomes a completely different life.</p></div>
            <div className="why-item"><h3><i>—</i>It protects your worst days</h3><p>Everyone has bad weeks. Discipline is what stops a bad week from becoming a bad year.</p></div>
            <div className="why-item"><h3><i>—</i>It's buildable at any starting point</h3><p>You don't need to already be disciplined. It's not a trait, it's a system you construct, one proof at a time.</p></div>
          </div>
        </div>
      </section>

      <section className="final-cta">
        <div className="wrap">
          <div className="eyebrow">Start today</div>
          <h2>You already know what to do.<br />This just makes sure you do it.</h2>
          <p>Start with the free guide, no cost, no catch, just seven prompts you can use tonight.</p>
          <Link to="/free-guide" className="btn btn-primary">Get the free AI prompts guide →</Link>
        </div>
      </section>

      <footer>
        <div className="wrap">
          <div className="footer-logo">disciplined.me</div>
          <div className="footer-links">
            <a href="#books">Free guide</a>
            <a href="#books">The bundle</a>
            <a href="#why">Why discipline</a>
          </div>
        </div>
        <div className="wrap footer-bottom">
          © Disciplined Me, Building the identity of someone who keeps going.
        </div>
      </footer>
    </div>
  );
};

export default Home;
